import { memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import isEqual from 'lodash-es/isEqual'

import { useAsyncCallback, useMouseHover } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { TableCellFreeSpaceFiller } from '../TableCellFreeSpaceFiller'
import { TableHeaderRowActions } from '../TableHeaderRowActions'
import { TableHeaderRowCell } from '../TableHeaderRowCell'

import { QA_NAME_TABLE_CONTEXT } from './TableHeaderRow.constants'
import { useColumnResize } from './TableHeaderRow.hooks'
import { getColumnTitle, getNextOrdering } from './TableHeaderRow.utils'

import * as styles from './TableHeaderRow.module.css'

function TableHeaderRow({
  Actions = TableHeaderRowActions,
  actionsColumnWidth,
  actionsProps = { className: styles.actionColumn },
  className,
  columnResizeConfig,
  columns,
  columnsConfig,
  emptyColumnSize,
  isResizing,
  ordering,
  qaName,
  resizable,
  scrollRef,
  showCellBorder,
  withCellBorder = true,
  onApply,
  onSetColumns,
  onSetOrdering,
  onSetResizing,
  onSetResizingColumn,
  onSetShowCellBorder,
}) {
  const { t } = useTranslation()

  const [ref, isHovered] = useMouseHover()

  const { curT, handleResize } = useColumnResize({
    columns,
    columnsConfig,
    config: columnResizeConfig,
    headerRef: ref,
    scrollRef,
    onApply,
    onSetColumns,
    onSetResizing,
  })

  useEffect(() => {
    if (withCellBorder) {
      onSetShowCellBorder?.(isHovered)
    }
  }, [isHovered])

  useEffect(() => {
    onSetResizingColumn(curT?.current)
  }, [isResizing])

  const handleClickOrder = useAsyncCallback(
    (fieldName, sortDirection) => {
      const nextOrdering = getNextOrdering(fieldName, sortDirection, ordering)

      if (isEqual(ordering, nextOrdering)) {
        return new Promise(resolve => resolve(null))
      }

      return onSetOrdering(nextOrdering)
    },
    [ordering, onSetOrdering]
  )

  const rowClassName = classNames(className, styles.TableHeaderRow, {
    [styles.resizing]: isResizing,
  })

  const qaPropsHeader = getQaSelector({
    context: QA_NAME_TABLE_CONTEXT,
    def: QA_NAME_TABLE_CONTEXT,
    value: qaName,
  })

  return (
    <div {...qaPropsHeader} className={rowClassName} ref={ref}>
      {actionsColumnWidth ? (
        <Actions {...actionsProps} width={actionsColumnWidth} />
      ) : null}
      {columns?.map(column => {
        const orderingField = column.ordering_field

        const title = getColumnTitle({ column, t })

        return (
          <TableHeaderRowCell
            className={column.className}
            columnName={column.name}
            isActive={curT?.current === column.name}
            isResizing={isResizing}
            key={column.name}
            ordering={ordering}
            orderingField={orderingField}
            resizable={resizable}
            showCellBorder={showCellBorder}
            title={title}
            width={column.width}
            onClick={handleClickOrder}
            onResize={handleResize}
          />
        )
      })}
      <TableCellFreeSpaceFiller width={emptyColumnSize} />
    </div>
  )
}

export default memo(TableHeaderRow)
